import "./instrument"; // Sentry must init before all other code
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import SentryFallback from "./SentryFallback";
import {
    attemptChunkRecoveryReload,
    isCSSChunkLoadError,
    isDestructuringUndefinedError,
    isLazyDefaultExportError,
    isStaleChunkSyntaxError,
    isViteModuleImportError,
    markVitePreloadError,
} from "./utils/chunkRecovery";
import { applyDevelopmentFavicon } from "./utils/devFavicon";
import { emitFrontendTelemetry } from "./utils/frontendTelemetry";
import logger from "./utils/logger";
applyDevelopmentFavicon();
window.React = React;

// Shim MediaQueryList.addListener/removeListener for environments where the
// deprecated API has been removed (Chrome 120+) or overridden by extensions.
// antd's internal ResponsiveObserver registers breakpoint listeners via
// addListener, which throws a TypeError when the method is undefined.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    const _nativeMatchMedia = window.matchMedia.bind(window);
    window.matchMedia = function patchedMatchMedia(query) {
        const mql = _nativeMatchMedia(query);
        if (mql && typeof mql.addListener !== "function") {
            mql.addListener = function (cb) {
                mql.addEventListener("change", cb);
            };
            mql.removeListener = function (cb) {
                mql.removeEventListener("change", cb);
            };
        }
        return mql;
    };
}

if (typeof window !== "undefined") {
    window.addEventListener("error", (event) => {
        if (isStaleChunkSyntaxError(event) || isCSSChunkLoadError(event) || isViteModuleImportError(event) || isLazyDefaultExportError(event) || isDestructuringUndefinedError(event)) {
            event.preventDefault?.();
            event.stopImmediatePropagation?.();
            attemptChunkRecoveryReload(event);
            return;
        } else {
            const resourceTarget = event.target && event.target !== window ? event.target : null;
            const resourceUrl = resourceTarget?.currentSrc
                || resourceTarget?.src
                || resourceTarget?.href
                || null;
            const resourceTag = String(resourceTarget?.tagName || "").trim().toUpperCase();
            const isResourceLoadError = Boolean(resourceUrl || resourceTag) && !event.error;
            void emitFrontendTelemetry("frontend.unhandled_error", {
                area: "window",
                action: isResourceLoadError ? "resource_load_error" : "error_event",
                url: event.filename || resourceUrl,
                message: event.error?.message
                    || event.message
                    || (isResourceLoadError ? `Failed to load ${resourceTag || "page"} resource` : "Unhandled window error"),
                errorCode: event.error?.name || (isResourceLoadError ? "ResourceLoadError" : null),
            });
        }

        attemptChunkRecoveryReload(event);
    }, true);

    // Vite also sends module evaluation errors through this event. Preserve the
    // original rejection: preventing it makes import() resolve to undefined,
    // which hides the cause behind a React.lazy "reading 'default'" failure.
    window.addEventListener("vite:preloadError", (event) => {
        const error = markVitePreloadError(event?.payload);
        attemptChunkRecoveryReload({
            message: error?.message || (error?.href
                ? `Failed to fetch dynamically imported module: ${error.href}`
                : typeof error === "string" ? error : ""),
            error,
        });
    });

    // CSS chunk load errors surface as rejected promises (from dynamic import()),
    // not as regular error events. Extract the reason and attempt recovery.
    window.addEventListener("unhandledrejection", (event) => {
        // Suppress AbortError from event-source-polyfill's FetchTransport.
        // Its abort() calls reader.cancel() without catching the returned promise,
        // which rejects when controller.abort() errors the stream immediately after.
        if (
            event.reason?.name === "AbortError" ||
            /signal is aborted/i.test(event.reason?.message)
        ) {
            event.preventDefault();
            return;
        }

        if (event.reason) {
            if (isCSSChunkLoadError({ reason: event.reason }) || isViteModuleImportError({ reason: event.reason }) || isLazyDefaultExportError({ reason: event.reason }) || isDestructuringUndefinedError({ reason: event.reason })) {
                event.preventDefault?.();
                event.stopImmediatePropagation?.();
                attemptChunkRecoveryReload({
                    message: event.reason?.message || (typeof event.reason === 'string' ? event.reason : ''),
                    error: event.reason,
                    reason: event.reason,
                });
                return;
            } else {
                void emitFrontendTelemetry("frontend.unhandled_rejection", {
                    area: "window",
                    action: "unhandled_rejection",
                    message: event.reason?.message || String(event.reason),
                    errorCode: event.reason?.name,
                });
            }

            attemptChunkRecoveryReload({
                message: event.reason?.message || (typeof event.reason === 'string' ? event.reason : ''),
                error: event.reason,
                reason: event.reason,
            });
        }
    });
}

try {
    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
        <React.StrictMode>
            <Sentry.ErrorBoundary
                fallback={SentryFallback}
                onError={(error) => {
                    if (isCSSChunkLoadError(error) || isViteModuleImportError(error) || isStaleChunkSyntaxError(error) || isLazyDefaultExportError(error) || isDestructuringUndefinedError(error)) {
                        attemptChunkRecoveryReload({ message: error?.message, error });
                        return;
                    }

                    void emitFrontendTelemetry("frontend.app.crash", {
                        area: "react",
                        action: "error_boundary",
                        message: error?.message,
                        errorCode: error?.name,
                    });
                }}
            >
                <App />
            </Sentry.ErrorBoundary>
        </React.StrictMode>
    );
} catch (error) {
    void emitFrontendTelemetry("frontend.bootstrap.failure", {
        area: "bootstrap",
        action: "render_failed",
        message: error?.message,
        errorCode: error?.name,
    });
    throw error;
}

// Send Web Vitals to Sentry as custom measurements (production only)
reportWebVitals((metric) => {
    const thresholds = {
        CLS: 0.25,
        INP: 500,
        LCP: 4000,
        FCP: 3000,
        TTFB: 1800,
    };

    const threshold = thresholds[metric.name];
    if (typeof threshold === "number" && metric.value > threshold) {
        logger.slowView({
            area: "performance",
            action: "web_vital_threshold_exceeded",
            metricName: metric.name,
            message: `${metric.name} exceeded threshold`,
            durationMs: metric.value,
            url: window.location.pathname,
        });
    }

    if (process.env.VITE_ENV === "production" && process.env.VITE_SENTRY_DSN) {
        Sentry.metrics.distribution(metric.name, metric.value, {
            unit: metric.name === "CLS" ? "" : "millisecond",
            tags: { metric_id: metric.id },
        });
    }
});
